/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

// Port of the "modern" rod from vpx-js lib/vpt/plunger/plunger-mesh-generator.ts,
// with the lathe list of plunger-desc.ts and plunger-coord.ts.
// Changes: input is a PlungerSpec; one rod, built once. A move of the tip only changes the
// y values of the rod (see rodTipPositions). The spring, the flat plunger and the custom
// plunger are not ported: the contract has no field for them.

import { Vertex3DNoTex2 } from "../../engine/math/vertex.ts";
import { Mesh } from "../../engine/mesh.ts";
import type { PlungerSpec } from "../../shared/contracts.ts";

/** One ring of the lathe. */
interface LatheCoord {
  /** Radius, as a fraction of the plunger width. */
  readonly r: number;
  /** Distance from the tip along the rod, in table units. */
  readonly y: number;
  readonly tv: number;
  readonly nx: number;
  readonly ny: number;
}

/** Rings of the modern plunger, from the tip to the shaft. */
const MODERN_LATHE: readonly LatheCoord[] = [
  { r: 0.2, y: 0.0, tv: 0.0, nx: 1.0, ny: 0.0 },
  { r: 0.3, y: 3.0, tv: 0.11, nx: 1.0, ny: 0.0 },
  { r: 0.35, y: 5.0, tv: 0.14, nx: 1.0, ny: 0.0 },
  { r: 0.35, y: 23.0, tv: 0.19, nx: 1.0, ny: 0.0 },
  { r: 0.45, y: 23.0, tv: 0.21, nx: 0.8, ny: 0.0 },
  { r: 0.25, y: 24.0, tv: 0.25, nx: 0.3, ny: 0.0 },
  { r: 0.25, y: 100.0, tv: 1.0, nx: 0.3, ny: 0.0 },
];

/** Vertices around the rod. */
export const PLUNGER_CIRCLE_POINTS = 24;
/** Rings along the rod. */
export const PLUNGER_LATHE_POINTS = MODERN_LATHE.length;

/** Default stroke when the spec has none. */
const STROKE_DEFAULT = 80;

/** Stroke of the plunger. */
export function plungerStroke(spec: PlungerSpec): number {
  return spec.stroke ?? STROKE_DEFAULT;
}

/** Table y of the rod base. This end stays fixed. */
export function plungerBaseY(spec: PlungerSpec): number {
  return spec.center.y + spec.height;
}

/** Table y of the tip at the rest position (the park position). */
export function plungerRestY(spec: PlungerSpec): number {
  const top = spec.center.y - plungerStroke(spec);
  const park = spec.parkPosition ?? 0.5;
  return top + park * plungerStroke(spec);
}

/**
 * Table y of the tip for a frame offset. The offset is the distance from the forward limit:
 * 0 is fully forward and the stroke length is fully back (PlungerState.offset in the engine).
 * An unset offset gives the rest position.
 */
export function plungerTipY(spec: PlungerSpec, offset: number | undefined): number {
  if (offset === undefined || !Number.isFinite(offset)) return plungerRestY(spec);
  const stroke = plungerStroke(spec);
  return spec.center.y - stroke + Math.min(stroke, Math.max(0, offset));
}

/**
 * Build the rod mesh for a tip position.
 * @param spec the plunger
 * @param tipY table y of the tip
 * @param zBase height of the surface under the plunger
 */
export function generatePlungerRod(spec: PlungerSpec, tipY: number, zBase: number): Mesh {
  const mesh = new Mesh(`plunger.rod-${spec.name}`);
  const lathePoints = PLUNGER_LATHE_POINTS;
  const circlePoints = PLUNGER_CIRCLE_POINTS;
  const baseY = plungerBaseY(spec);
  const zHeight = zBase + (spec.zAdjust ?? 0);

  const stepU = 1.0 / circlePoints;
  let tu = 0.51;
  for (let l = 0; l < circlePoints; l++, tu += stepU) {
    if (tu > 1.0) tu -= 1.0;
    const angle = ((Math.PI * 2.0) / circlePoints) * l;
    const sn = Math.sin(angle);
    const cs = Math.cos(angle);
    for (let m = 0; m < lathePoints; m++) {
      const c = MODERN_LATHE[m] as LatheCoord;
      const last = m + 1 === lathePoints;
      const v = new Vertex3DNoTex2();
      v.x = c.r * (sn * spec.width) + spec.center.x;
      // The last ring is the base of the rod. It does not move.
      v.y = last ? baseY : c.y + tipY;
      v.z = c.r * (cs * spec.width) + spec.width + zHeight;
      v.nx = c.nx * sn;
      v.ny = c.ny;
      v.nz = -c.nx * cs;
      v.tu = tu;
      v.tv = c.tv;
      mesh.vertices.push(v);
    }
  }

  const latheVertices = lathePoints * circlePoints;
  for (let l = 0, offset = 0; l < circlePoints; l++, offset += lathePoints) {
    for (let m = 0; m < lathePoints - 1; m++) {
      mesh.indices.push(
        (m + offset) % latheVertices,
        (m + offset + lathePoints) % latheVertices,
        (m + offset + 1 + lathePoints) % latheVertices,
        (m + offset + 1 + lathePoints) % latheVertices,
        (m + offset + 1) % latheVertices,
        (m + offset) % latheVertices,
      );
    }
  }
  return mesh;
}

/**
 * Table y of every rod vertex for a tip position, in vertex order of `generatePlungerRod`.
 * The renderer writes these values to the z of the position attribute (table y is three z).
 */
export function rodTipPositions(spec: PlungerSpec, tipY: number): number[] {
  const out: number[] = [];
  const baseY = plungerBaseY(spec);
  for (let l = 0; l < PLUNGER_CIRCLE_POINTS; l++) {
    for (let m = 0; m < PLUNGER_LATHE_POINTS; m++) {
      const last = m + 1 === PLUNGER_LATHE_POINTS;
      out.push(last ? baseY : (MODERN_LATHE[m] as LatheCoord).y + tipY);
    }
  }
  return out;
}
